import { FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { BrandGroup } from '@provision/entity-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { appendCodeToPhoneNumber } from 'src/components/Forms/utils';
import { Loader } from 'src/components/Loader/loader';
import { SAVE_DEBOUNCE_DELAY } from 'src/constants/delays';
import {
  BrandGroupUpdatePatch,
  useBrandGroup,
  useBrandGroups,
} from 'src/graphql/entities/brand-group';
import { useGlobalStore } from 'src/store';
import useDebouncedValue from 'src/utils/hooks/use-debounced-value';
import { ChannelsVoiceForm } from './channels-voice-form';
import { VoiceChannelSettingFormFields, VoiceFormModel } from './types';

type ParsedVoiceFormValues = BrandGroupUpdatePatch;

export const ChannelsVoiceView = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { brandGroups } = useBrandGroups(selectedPartnerId);
  const [brandGroup, setBrandGroup] = useState<BrandGroup | null>(null);
  const [pendingUpdate, setPendingUpdate] = useState<ParsedVoiceFormValues | null>(null);
  const brandGroupRef = useRef<BrandGroup | null>(null);
  const debouncedUpdate = useDebouncedValue(pendingUpdate, SAVE_DEBOUNCE_DELAY);

  useEffect(() => {
    if (!brandGroups.length) {
      return;
    }

    setBrandGroup(brandGroups[0]);
  }, [brandGroups, setBrandGroup]);

  useEffect(() => {
    brandGroupRef.current = brandGroup;
  }, [brandGroup]);

  useEffect(() => {
    setPendingUpdate(null);
  }, [brandGroup?.id]);

  const { updateBrandGroup } = useBrandGroup(selectedPartnerId);

  const handleSave = useCallback((formValue: VoiceFormModel) => {
    const value = Object.fromEntries(
      Object.entries(formValue).map(([key, value]) => [
        key,
        parsePhoneNumber(key as keyof VoiceFormModel, value),
      ]),
    ) as ParsedVoiceFormValues;

    // Clearing the published number must also clear its extension. Saves are
    // per-changed-field, so without this a stale ext can reattach when a new
    // published number is set later.
    if (
      VoiceChannelSettingFormFields.PhonePublished in formValue &&
      !formValue[VoiceChannelSettingFormFields.PhonePublished]
    ) {
      value[VoiceChannelSettingFormFields.PhonePublishedExt] = null;
    }

    setBrandGroup((prev) => (prev ? ({ ...prev, ...value } as BrandGroup) : prev));
    setPendingUpdate((prev) => ({
      ...(prev ?? {}),
      ...value,
    }));
  }, []);

  useEffect(() => {
    if (!debouncedUpdate) {
      return;
    }

    const currentBrandGroup = brandGroupRef.current;
    void updateBrandGroup({
      ...debouncedUpdate,
      id: currentBrandGroup?.id ?? debouncedUpdate.id?.toString(),
    });
    setPendingUpdate(null);
  }, [debouncedUpdate, updateBrandGroup]);

  const handleBrandGroupChange = useCallback(
    (brandGroupId: string) => {
      const selectedBrandGroup = brandGroups.find((item) => item.id === brandGroupId);
      setBrandGroup(selectedBrandGroup!);
    },
    [brandGroups, setBrandGroup],
  );

  return brandGroups?.length ? (
    <Stack gap={1}>
      {brandGroups?.length > 1 ? (
        <FormControl
          sx={{
            width: 'max-content',
          }}
        >
          <InputLabel id="brand-group-label">Brand Group</InputLabel>
          <Select
            labelId="brand-group-label"
            value={brandGroup?.id ?? ''}
            id="settings-email-brand-group"
            label="Brand Group"
            onChange={(event) => {
              handleBrandGroupChange(event.target.value);
            }}
          >
            {brandGroups.map((brandGroup) => (
              <MenuItem key={brandGroup.id} value={brandGroup.id}>
                {brandGroup.brandGroupName || 'Not defined'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
      {brandGroup ? (
        <Stack p={1} gap={1} sx={{ maxWidth: 600 }}>
          <Typography variant="subtitle1" sx={{ marginBottom: '1rem' }}>
            Configure the phone numbers used for support
          </Typography>
          <ChannelsVoiceForm handleSave={handleSave} selectedBrandGroup={brandGroup} />
        </Stack>
      ) : null}
    </Stack>
  ) : (
    <Loader />
  );
};

function parsePhoneNumber(key: keyof VoiceFormModel, value: string) {
  if (key === VoiceChannelSettingFormFields.Id) {
    return value;
  }

  if (!value) {
    return null;
  }

  if (key === VoiceChannelSettingFormFields.PhonePublishedExt) {
    const parsedExt = parseInt(value, 10);
    return Number.isNaN(parsedExt) ? null : parsedExt;
  }

  if (key === VoiceChannelSettingFormFields.CallerIdPhone) {
    return parseInt(appendCodeToPhoneNumber(value));
  }

  return parseInt(value);
}
